import type { MembershipIncomeUpsertDto } from '@fruitful/member-profile';
import React, { useEffect, useRef, useState } from 'react';
import {
    DollarInput,
    Input,
    Spinner,
    SpinnerSize,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import type { MembershipDetails } from '../../../entities/entityTypes';
import { captureException } from '../../../logger/Logger';
import { GroveMoneyMapPickerField } from '../GroveMoneyMapPickerField';
import {
    isFocusMovingWithinMoneyMapTableRow,
    type MoneyMapTableRowBlurEvent,
} from '../moneyMapperTableRowFocus';
import {
    emptyForm,
    INCOME_SAVED_TOAST_DEBOUNCE_MS,
    type IncomeDetailsFormState,
    type IncomeTypePickerItem,
    type OwnerPickerItem,
    type PayCyclePickerItem,
} from './moneyMapperIncomeForm';
import {
    isIncomePeriodAmountInvalid,
    isIncomeSourceEmpty,
    persistMembershipIncomeFromForm,
    validateIncomeFormForUpsert,
} from './moneyMapperIncomePersist';
import {
    INCOME_TABLE_ACTIONS_OUTER_WIDTH,
    INCOME_TABLE_ROW_PADDING_HORIZONTAL,
    incomeTableAmountColumnProps,
    incomeTableIncomeTypeColumnProps,
    incomeTableMemberColumnProps,
    incomeTablePayCycleColumnProps,
    incomeTableRowFocusActive,
    incomeTableSourceColumnProps,
} from './moneyMapperIncomeTableLayout';

export interface MainIncomeNewRowProps {
    membership: MembershipDetails;
    ownerOptions: Record<string, string>;
    ownerPickerItems: OwnerPickerItem[];
    payCyclePickerItems: PayCyclePickerItem[];
    incomeTypePickerItems: IncomeTypePickerItem[];
    canMutate: boolean;
    moneyMapSaving: boolean;
    isModalBusy: boolean;
    upsertMembershipIncomeAsync: (
        id: string,
        body: MembershipIncomeUpsertDto[],
    ) => Promise<unknown>;
    refetchMembershipAndSyncMoneyMap: () => Promise<void>;
    onSaved: () => void;
}

export const MainIncomeNewRow = ({
    membership,
    ownerOptions,
    ownerPickerItems,
    payCyclePickerItems,
    incomeTypePickerItems,
    canMutate,
    moneyMapSaving,
    isModalBusy,
    upsertMembershipIncomeAsync,
    refetchMembershipAndSyncMoneyMap,
    onSaved,
}: MainIncomeNewRowProps) => {
    const toast = useToastController();

    const [draft, setDraft] = useState<IncomeDetailsFormState>(() =>
        emptyForm(ownerOptions),
    );
    const [showSourceInvalid, setShowSourceInvalid] = useState(false);
    const [showAmountInvalid, setShowAmountInvalid] = useState(false);
    const [isRowSaving, setIsRowSaving] = useState(false);
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const lastIncomeSavedToastAtRef = useRef(0);
    const isMountedRef = useRef(true);
    const tableRowRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const disabled =
        !canMutate ||
        moneyMapSaving ||
        isModalBusy ||
        isRowSaving ||
        ownerPickerItems.length === 0;

    const showMemberDropdown = ownerPickerItems.length > 1;

    const tryCreate = async (next: IncomeDetailsFormState) => {
        if (!canMutate || moneyMapSaving || isModalBusy || isRowSaving) {
            return;
        }
        const validation = validateIncomeFormForUpsert(next);
        if (!validation.ok) {
            return;
        }

        setIsRowSaving(true);
        try {
            await persistMembershipIncomeFromForm({
                upsertMembershipIncomeAsync,
                refetchMembershipAndSyncMoneyMap,
                membershipId: membership.id,
                existing: null,
                form: next,
                afterUpsertBeforeRefetch: onSaved,
            });
            const now = Date.now();
            if (
                now - lastIncomeSavedToastAtRef.current >=
                INCOME_SAVED_TOAST_DEBOUNCE_MS
            ) {
                lastIncomeSavedToastAtRef.current = now;
                toast.show('Income source saved', MutationSuccessToastOptions);
            }
        } catch (error) {
            captureException(error);
            toast.show('Error saving income', MutationErrorToastOptions(error));
        } finally {
            if (isMountedRef.current) {
                setIsRowSaving(false);
            }
        }
    };

    const persistIfLeavingRow = (event: MoneyMapTableRowBlurEvent) => {
        const root = tableRowRef.current as unknown as
            | Element
            | null
            | undefined;
        if (isFocusMovingWithinMoneyMapTableRow(root, event)) {
            return;
        }
        void tryCreate(draftRef.current);
    };

    return (
        <XStack
            ref={tableRowRef}
            width="100%"
            alignItems="flex-start"
            paddingVertical={12}
            paddingHorizontal={INCOME_TABLE_ROW_PADDING_HORIZONTAL}
            gap={8}
            {...incomeTableRowFocusActive}
        >
            {showMemberDropdown && (
                <YStack {...incomeTableMemberColumnProps}>
                    <GroveMoneyMapPickerField
                        autoFocusOnMount
                        size="regular"
                        value={draft.belongsToUserId}
                        options={ownerPickerItems}
                        disabled={disabled}
                        onTriggerBlur={persistIfLeavingRow}
                        onChange={(v) => {
                            const next = { ...draft, belongsToUserId: v };
                            setDraft(next);
                            void tryCreate(next);
                        }}
                    />
                </YStack>
            )}
            <YStack {...incomeTableSourceColumnProps}>
                <Input
                    size="regular"
                    // Intentional: single-member add-income row focuses Source so users can type immediately; joint uses member picker focus instead.
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- see above
                    autoFocus={!showMemberDropdown}
                    value={draft.description}
                    invalid={showSourceInvalid}
                    onChangeText={(t) => {
                        setDraft((d) => ({ ...d, description: t }));
                        if (t.trim().length > 0) {
                            setShowSourceInvalid(false);
                        }
                    }}
                    onBlur={(e) => {
                        setShowSourceInvalid(
                            isIncomeSourceEmpty(draftRef.current.description),
                        );
                        if (
                            !isFocusMovingWithinMoneyMapTableRow(
                                tableRowRef.current as unknown as Element | null,
                                e,
                            )
                        ) {
                            void tryCreate(draftRef.current);
                        }
                    }}
                    placeholder="Source"
                    disabled={disabled}
                />
            </YStack>
            <YStack {...incomeTableAmountColumnProps}>
                <DollarInput
                    size="regular"
                    value={draft.periodAmountStr}
                    invalid={showAmountInvalid}
                    onChangeText={(t) => {
                        setDraft((d) => ({ ...d, periodAmountStr: t }));
                        if (!isIncomePeriodAmountInvalid(t)) {
                            setShowAmountInvalid(false);
                        }
                    }}
                    onBlur={(e) => {
                        setShowAmountInvalid(
                            isIncomePeriodAmountInvalid(
                                draftRef.current.periodAmountStr,
                            ),
                        );
                        if (
                            !isFocusMovingWithinMoneyMapTableRow(
                                tableRowRef.current as unknown as Element | null,
                                e,
                            )
                        ) {
                            void tryCreate(draftRef.current);
                        }
                    }}
                    allowDecimals={false}
                    disabled={disabled}
                />
            </YStack>
            <YStack {...incomeTablePayCycleColumnProps}>
                <GroveMoneyMapPickerField
                    size="regular"
                    value={draft.payCycle}
                    options={payCyclePickerItems}
                    disabled={disabled}
                    onTriggerBlur={persistIfLeavingRow}
                    onChange={(v) => {
                        const next = { ...draft, payCycle: v };
                        setDraft(next);
                        void tryCreate(next);
                    }}
                />
            </YStack>
            <YStack {...incomeTableIncomeTypeColumnProps}>
                <GroveMoneyMapPickerField
                    size="regular"
                    value={draft.type}
                    options={incomeTypePickerItems}
                    disabled={disabled}
                    onTriggerBlur={persistIfLeavingRow}
                    onChange={(v) => {
                        const next = { ...draft, type: v };
                        setDraft(next);
                        void tryCreate(next);
                    }}
                />
            </YStack>
            <YStack
                width={INCOME_TABLE_ACTIONS_OUTER_WIDTH}
                flexShrink={0}
                minHeight={44}
                alignItems="flex-end"
                justifyContent="center"
                paddingTop={6}
            >
                {isRowSaving && <Spinner size={SpinnerSize.SMALL} />}
            </YStack>
        </XStack>
    );
};
